import { Injectable } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from '@env';

/**
 * Wrapper test-safe autour de PostHog (posthog-js) pour le back-office admin.
 *
 * PostHog est initialise une seule fois dans `main.ts` (error tracking +
 * session replay + analytics). Ce service est l'unique point d'entree pour
 * les call sites Angular (LoggerService, GlobalErrorHandler) — il devient un
 * no-op si `environment.posthogKey` est vide (dev sans cle, tests, prod avant
 * cutover).
 *
 * Admin = CSR pur (pas de SSR), donc pas de garde `isPlatformBrowser`.
 *
 * Anti-lock-in : le reste de l'app depend de ce wrapper, jamais de
 * `posthog-js` directement.
 */
@Injectable({ providedIn: 'root' })
export class TelemetryService {
  /** True quand PostHog est actif (cle renseignee). */
  get enabled(): boolean {
    return Boolean(environment.posthogKey);
  }

  /**
   * Forward une exception a PostHog (error tracking). No-op sans cle.
   * `context` est attache comme proprietes pour aider le triage.
   */
  captureException(error: unknown, context?: Record<string, unknown>): void {
    if (!this.enabled) return;
    const err = error instanceof Error ? error : new Error(String(error));
    posthog.captureException(err, context);
  }

  /**
   * Sentry utilisait des breadcrumbs pour le contexte pre-erreur. PostHog n'a
   * pas cette notion : le SESSION REPLAY capture deja tout le contexte de
   * session (clics, navigation, reseau) — c'est donc un no-op volontaire.
   */
  addBreadcrumb(
    _category: string,
    _message: string,
    _level: 'info' | 'warning' | 'error' = 'info'
  ): void {
    // no-op : couvert par le session replay PostHog.
  }

  /**
   * Forward un message arbitraire (typiquement un `warn`/`error` qui ne crash
   * pas) comme event PostHog. No-op sans cle.
   */
  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
    if (!this.enabled) return;
    posthog.capture('frontend_message', { message, level });
  }
}
